import {
  ConnectionErrorCode,
  ConnectionStatus,
} from "../../../api/integrations";

// Shared status logic so the overview and the detail view agree on what an
// error state means (SKE-3955). The connection state machine (SKE-3948) lumps two
// very different failures under `status === "error"`: a credential problem the
// user fixes by RECONNECTING, and `sync_failing` where the credentials are fine
// but syncs keep erroring — reconnecting would NOT help. The UI must never tell a
// `sync_failing` connection to reconnect, so both surfaces route through here.

// The credential-error family: reconnecting resolves these. `sync_failing` is
// deliberately excluded. An `error` with no code is treated as a credential error,
// since reconnect is the safe default when we can't tell what broke.
const CREDENTIAL_ERROR_CODES = new Set<ConnectionErrorCode>([
  "invalid_credentials",
  "refresh_failed",
  "unhealthy",
]);

export function isCredentialError(code?: ConnectionErrorCode): boolean {
  return code === undefined || CREDENTIAL_ERROR_CODES.has(code);
}

// True only when reconnecting is the right action: the connection is errored AND
// the error is a credential problem (not `sync_failing`).
export function needsReconnect(connection: {
  status: ConnectionStatus;
  error?: { code?: ConnectionErrorCode };
}): boolean {
  return (
    connection.status === "error" && isCredentialError(connection.error?.code)
  );
}

// True when the connection is errored but its access is fine — syncs keep failing
// and retrying. The amber "review the activity feed" case, never a red reconnect.
export function isSyncFailing(connection: {
  status: ConnectionStatus;
  error?: { code?: ConnectionErrorCode };
}): boolean {
  return (
    connection.status === "error" && connection.error?.code === "sync_failing"
  );
}
